import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram } from 'lucide-react';
import { Button } from './ui/button';

const navigationLinks = [
  { label: "About", href: "/#about" },
  { label: "Services", href: "/#services" },
  { label: "Portfolio", href: "/#portfolio" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/#contact" }
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_visuals-by-kate/artifacts/t0c5kpd1_IMG_0438.jpeg";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleNavClick = (href) => {
    setIsMenuOpen(false);
    
    // If it's a hash link on the current page
    if (href.startsWith('/#') && location.pathname === '/') {
      const element = document.querySelector(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const renderNavLink = (link) => {
    const isExternal = link.href.startsWith('/#');
    const isActive = location.pathname === link.href || 
                     (link.href === '/blog' && location.pathname.startsWith('/blog'));

    if (isExternal) {
      return (
        <Link
          key={link.label}
          to={link.href}
          onClick={() => handleNavClick(link.href)}
          className={`text-sm font-medium relative group transition-colors ${
            isActive ? 'text-[#c9a962]' : 'text-[#b3b3b3] hover:text-[#c9a962]'
          }`}
        >
          {link.label}
          <span className="absolute bottom-0 left-0 w-0 h-px bg-[#c9a962] transition-all duration-300 group-hover:w-full" />
        </Link>
      );
    }

    return (
      <Link
        key={link.label}
        to={link.href}
        className={`text-sm font-medium relative group transition-colors ${
          isActive ? 'text-[#c9a962]' : 'text-[#b3b3b3] hover:text-[#c9a962]'
        }`}
      >
        {link.label}
        <span className={`absolute bottom-0 left-0 h-px bg-[#c9a962] transition-all duration-300 ${
          isActive ? 'w-full' : 'w-0 group-hover:w-full'
        }`} />
      </Link>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#2a2a2a]">
      <div className="container">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img 
              src={LOGO_URL} 
              alt="Kate Samuilova - Social Growth" 
              className="h-16 md:h-20 w-auto object-contain"
              style={{ 
                mixBlendMode: 'lighten'
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigationLinks.map(renderNavLink)}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="https://instagram.com/katesamuilova" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#b3b3b3] hover:text-[#c9a962] transition-colors"
            >
              <Instagram size={20} />
            </a>
            <Button 
              asChild
              className="bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none px-6 py-2 text-sm font-medium transition-all duration-300"
            >
              <Link to="/#contact">Get in Touch</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-[#0a0a0a] border-b border-[#2a2a2a] py-6">
            <div className="container flex flex-col gap-4">
              {navigationLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-base font-medium text-[#b3b3b3] py-2 border-b border-[#1a1a1a] hover:text-[#c9a962] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center gap-4 pt-4">
                <a 
                  href="https://instagram.com/katesamuilova" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#b3b3b3] hover:text-[#c9a962]"
                >
                  <Instagram size={20} />
                </a>
                <Button 
                  asChild
                  className="bg-[#c9a962] text-[#0a0a0a] border-none rounded-none px-6 py-2 text-sm font-medium"
                >
                  <Link to="/#contact" onClick={() => setIsMenuOpen(false)}>Get in Touch</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
